import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import AdminHeader from '../components/AdminHeader';
import AdminTabs from '../components/AdminTabs';
import Toast from '../components/Toast';
import { API_BASE_URL } from '../config/api';
import { fetchCategories } from '../utils/categories';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function AddDesignPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    designId: '',
    title: '',
    description: '',
    orientation: 'horizontal',
    majorCategory: '',
    minorTags: '',
    price: '',
    product_type: 'Physical',
    isMockupOnly: false
  });
  const [designImage, setDesignImage] = useState(null);
  const [designImagePreview, setDesignImagePreview] = useState(null);
  const [designImageFilename, setDesignImageFilename] = useState('');
  const [designImageFromPath, setDesignImageFromPath] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [existingDesignId, setExistingDesignId] = useState(null);
  const [mockups, setMockups] = useState([]);
  const [majorCategories, setMajorCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    document.title = 'Add Design - Admin';
    // Load categories
    fetchCategories().then(categories => {
      setMajorCategories(categories.categories.map(c => c.name));
    });
  }, []);

  // Pre-fill form if design data was passed from JSON Editor
  useEffect(() => {
    if (location.state?.prefilledDesign) {
      const design = location.state.prefilledDesign;
      
      // Check if this is an existing design (edit mode)
      const checkIfDesignExists = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/admin/designs/${design.id}`, {
            headers: getAuthHeaders()
          });
          if (response.data.success) {
            const fullDesign = response.data.data;
            setIsEditMode(true);
            setExistingDesignId(design.id);
            
            // Set image info from the fetched design (which has master_file_path)
            if (fullDesign.master_file_path && fullDesign.id) {
              // Extract filename from path
              const filename = fullDesign.master_file_path.split('/').pop() || fullDesign.master_file_path.split('\\').pop() || '';
              setDesignImageFilename(filename);
              setDesignImageFromPath(true);
              
              // Load image preview from API
              const imageUrl = `${API_BASE_URL}/designs/${fullDesign.id}/image`;
              setDesignImagePreview(imageUrl);
            }
          }
        } catch (err) {
          // Design doesn't exist or error - treat as new design
          if (err.response?.status !== 404) {
            console.error('Error checking if design exists:', err);
          }
        }
      };
      
      if (design.id) {
        checkIfDesignExists();
      }
      
      // Extract tags
      let majorCategory = '';
      let minorTags = '';
      if (design.tags) {
        if (typeof design.tags === 'object' && design.tags.majorCategory) {
          majorCategory = design.tags.majorCategory || '';
          minorTags = Array.isArray(design.tags.minorTags) 
            ? design.tags.minorTags.join(', ') 
            : '';
        }
      }

      // Pre-fill form data
      setFormData({
        designId: design.id || '',
        title: design.title || '',
        description: design.description || '',
        orientation: design.orientation || 'horizontal',
        majorCategory: majorCategory,
        minorTags: minorTags,
        price: design.price !== undefined && design.price !== null ? design.price.toString() : '',
        product_type: design.product_type || 'Physical',
        isMockupOnly: design.price !== undefined && design.price !== null && design.price > 0
      });

      // Load design image preview if master_file_path exists
      if (design.master_file_path && design.id) {
        // Extract filename from path
        const filename = design.master_file_path.split('/').pop() || design.master_file_path.split('\\').pop() || '';
        setDesignImageFilename(filename);
        setDesignImageFromPath(true);
        
        // Load image preview from API
        const imageUrl = `${API_BASE_URL}/designs/${design.id}/image`;
        setDesignImagePreview(imageUrl);
      }

      // Pre-fill mockups if they exist
      if (design.mockups && Array.isArray(design.mockups) && design.mockups.length > 0) {
        const mockupList = design.mockups.map((mockup, index) => {
          // Build preview URL from image_url
          let previewUrl = null;
          if (mockup.image_url) {
            // Convert /assets/mockups/ to API endpoint or use as-is if already API URL
            if (mockup.image_url.startsWith('/assets/mockups/')) {
              const filename = mockup.image_url.replace('/assets/mockups/', '');
              previewUrl = `${API_BASE_URL}/mockups/${filename}`;
            } else if (mockup.image_url.startsWith('/api/mockups/')) {
              previewUrl = `${API_BASE_URL}${mockup.image_url}`;
            } else if (mockup.image_url.startsWith('http')) {
              previewUrl = mockup.image_url;
            }
          }
          
          return {
            name: mockup.name || '',
            image: null,
            preview: previewUrl,
            imageUrl: mockup.image_url || '',
            index: Date.now() + index,
            fromPath: !!mockup.image_url
          };
        });
        setMockups(mockupList);
      }

      // Clear location state to prevent re-prefilling on re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleDesignImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setToast({ message: 'Invalid file type. Only PNG, JPEG, GIF, and WebP images are allowed.', type: 'error' });
        return;
      }

      // Validate file size (50MB limit)
      if (file.size > 50 * 1024 * 1024) {
        setToast({ message: 'File size exceeds 50MB limit.', type: 'error' });
        return;
      }

      setDesignImage(file);
      setDesignImageFilename(file.name);
      setDesignImageFromPath(false);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setDesignImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddMockup = () => {
    setMockups([...mockups, { name: '', image: null, preview: null, index: Date.now() }]);
  };

  const handleAddProductMockup = () => {
    // Add a mockup with name "Product" using the design image
    // We need an actual file to use as the mockup image
    if (!designImage) {
      setToast({ message: 'Please upload a design image file first to use as the Product mockup. In edit mode, upload a new design image to use it for the Product mockup.', type: 'error' });
      return;
    }

    if (!designImagePreview) {
      setToast({ message: 'Design image preview not available.', type: 'error' });
      return;
    }

    // Create a new mockup entry using the design image file
    const newMockup = {
      name: 'Product',
      image: designImage, // Use the design image file
      preview: designImagePreview, // Use the design preview
      index: Date.now(),
      fromPath: false, // Always false since we're using the uploaded file
      imageUrl: null
    };

    setMockups([...mockups, newMockup]);
    
    // Clear error if it exists
    if (errors.mockups) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.mockups;
        return newErrors;
      });
    }
  };

  const handleRemoveMockup = (index) => {
    setMockups(mockups.filter((m, i) => m.index !== index));
  };

  const handleMockupChange = (index, field, value) => {
    setMockups(mockups.map((m, i) => {
      if (i === index) {
        if (field === 'image' && value instanceof File) {
          // Validate file
          const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
          if (!allowedTypes.includes(value.type)) {
            setToast({ message: 'Invalid mockup file type. Only PNG, JPEG, GIF, and WebP images are allowed.', type: 'error' });
            return m;
          }
          if (value.size > 50 * 1024 * 1024) {
            setToast({ message: 'Mockup file size exceeds 50MB limit.', type: 'error' });
            return m;
          }

          // Create preview
          const reader = new FileReader();
          reader.onloadend = () => {
            setMockups(prev => prev.map((mock, idx) => 
              idx === index ? { ...mock, preview: reader.result, fromPath: false } : mock
            ));
          };
          reader.readAsDataURL(value);
          return { ...m, image: value, fromPath: false };
        }
        return { ...m, [field]: value };
      }
      return m;
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.designId.trim()) {
      newErrors.designId = 'Design ID is required';
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.designId)) {
      newErrors.designId = 'Design ID can only contain letters, numbers, underscores, and hyphens';
    }

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.orientation) {
      newErrors.orientation = 'Orientation is required';
    }

    // In edit mode, image is optional if existing image exists
    // In create mode, image is required
    if (!isEditMode && !designImage) {
      newErrors.designImage = 'Design image is required';
    } else if (isEditMode && !designImage && !designImageFromPath) {
      newErrors.designImage = 'Design image is required (upload new or existing image must be present)';
    }

    if (formData.isMockupOnly) {
      if (!formData.price || parseFloat(formData.price) < 0) {
        newErrors.price = 'Valid price is required for mockup-only designs';
      }
      // Require at least one mockup for mockup-only designs
      if (mockups.length === 0) {
        newErrors.mockups = 'At least one mockup is required for mockup-only designs';
      }
    }

    // Validate mockups
    mockups.forEach((mockup, index) => {
      if (!mockup.name.trim()) {
        newErrors[`mockup_${index}_name`] = 'Mockup name is required';
      }
      // In edit mode, allow existing mockups (fromPath) without requiring upload
      // In create mode, require upload for new designs
      if (!isEditMode && !mockup.image) {
        newErrors[`mockup_${index}_image`] = 'Mockup image is required for new designs';
      } else if (isEditMode && !mockup.image && !mockup.fromPath) {
        newErrors[`mockup_${index}_image`] = 'Mockup image is required (upload new or existing image must be present)';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setToast({ message: 'Please fix the errors in the form.', type: 'error' });
      return;
    }

    setUploading(true);
    setToast(null);

    try {
      let designId;
      
      if (isEditMode && existingDesignId) {
        // Update existing design
        const formDataToSend = new FormData();
        
        // Only append designImage if a new file was uploaded
        if (designImage) {
          formDataToSend.append('designImage', designImage);
        }
        
        formDataToSend.append('title', formData.title.trim());
        formDataToSend.append('description', formData.description.trim());
        formDataToSend.append('orientation', formData.orientation);
        if (formData.majorCategory) {
          formDataToSend.append('majorCategory', formData.majorCategory);
        }
        if (formData.minorTags) {
          formDataToSend.append('minorTags', formData.minorTags);
        }
        if (formData.isMockupOnly && formData.price) {
          formDataToSend.append('price', formData.price);
          formDataToSend.append('product_type', formData.product_type);
        } else {
          formDataToSend.append('price', '');
        }

        const response = await axios.put(
          `${API_BASE_URL}/admin/designs/${existingDesignId}/upload`,
          formDataToSend,
          {
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        if (response.data.success) {
          designId = response.data.data.id;
        } else {
          setToast({ message: response.data.error || 'Failed to update design', type: 'error' });
          setUploading(false);
          return;
        }
      } else {
        // Create new design
        const formDataToSend = new FormData();
        
        // Require design image upload for new designs
        if (!designImage) {
          setToast({ message: 'Please upload a design image.', type: 'error' });
          setUploading(false);
          return;
        }
        
        formDataToSend.append('designImage', designImage);
        formDataToSend.append('designId', formData.designId.trim());
        formDataToSend.append('title', formData.title.trim());
        formDataToSend.append('description', formData.description.trim());
        formDataToSend.append('orientation', formData.orientation);
        if (formData.majorCategory) {
          formDataToSend.append('majorCategory', formData.majorCategory);
        }
        if (formData.minorTags) {
          formDataToSend.append('minorTags', formData.minorTags);
        }
        if (formData.isMockupOnly && formData.price) {
          formDataToSend.append('price', formData.price);
          formDataToSend.append('product_type', formData.product_type);
        }
        formDataToSend.append('mockups', JSON.stringify([])); // Will add mockups separately

        const response = await axios.post(
          `${API_BASE_URL}/admin/designs/upload`,
          formDataToSend,
          {
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        if (response.data.success) {
          designId = response.data.data.id;
        } else {
          setToast({ message: response.data.error || 'Failed to create design', type: 'error' });
          setUploading(false);
          return;
        }
      }

      if (designId) {

        // Handle mockups
        if (mockups.length > 0) {
          if (isEditMode) {
            // In edit mode: upload new mockup images, then replace entire mockups array
            // First, upload any new mockup images
            const uploadedMockupUrls = [];
            
            for (let index = 0; index < mockups.length; index++) {
              const mockup = mockups[index];
              
              if (mockup.image) {
                // New image uploaded - upload it
                const mockupFormData = new FormData();
                mockupFormData.append('mockupImage', mockup.image);
                mockupFormData.append('name', mockup.name.trim());
                mockupFormData.append('mockupIndex', index);

                const uploadResponse = await axios.post(
                  `${API_BASE_URL}/admin/designs/${designId}/mockups/upload`,
                  mockupFormData,
                  {
                    headers: {
                      ...getAuthHeaders(),
                      'Content-Type': 'multipart/form-data'
                    }
                  }
                );
                
                if (uploadResponse.data.success) {
                  // Get the newly uploaded mockup from response (last one in array)
                  const updatedDesign = uploadResponse.data.data;
                  if (updatedDesign.mockups && updatedDesign.mockups.length > 0) {
                    const newMockup = updatedDesign.mockups[updatedDesign.mockups.length - 1];
                    uploadedMockupUrls[index] = newMockup.image_url;
                  }
                }
              }
            }
            
            // Build final mockups array from form data
            const finalMockups = mockups.map((mockup, index) => {
              if (mockup.image && uploadedMockupUrls[index]) {
                // Use newly uploaded mockup URL
                return {
                  name: mockup.name.trim(),
                  image_url: uploadedMockupUrls[index],
                  thumbnail_url: uploadedMockupUrls[index]
                };
              } else if (mockup.fromPath && mockup.imageUrl) {
                // Keep existing mockup - use existing image_url, update name if changed
                return {
                  name: mockup.name.trim(),
                  image_url: mockup.imageUrl,
                  thumbnail_url: mockup.imageUrl
                };
              }
              return null;
            }).filter(m => m !== null);
            
            // Update design with all mockups (replace entire array)
            await axios.put(
              `${API_BASE_URL}/designs/${designId}`,
              { mockups: finalMockups },
              { headers: getAuthHeaders() }
            );
          } else {
            // Create mode - upload mockups sequentially
            for (let index = 0; index < mockups.length; index++) {
              const mockup = mockups[index];
              
              // Require upload for new designs
              if (!mockup.image) {
                setToast({ 
                  message: `Mockup "${mockup.name}" skipped - please upload an image file for new designs.`, 
                  type: 'warning' 
                });
                continue;
              }
              
              // Upload new mockup image
              const mockupFormData = new FormData();
              mockupFormData.append('mockupImage', mockup.image);
              mockupFormData.append('name', mockup.name.trim());
              mockupFormData.append('mockupIndex', index);

              await axios.post(
                `${API_BASE_URL}/admin/designs/${designId}/mockups/upload`,
                mockupFormData,
                {
                  headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'multipart/form-data'
                  }
                }
              );
            }
          }
        }

        setToast({ message: isEditMode ? 'Design updated successfully!' : 'Design created successfully!', type: 'success' });
        
        // Reset form
        setFormData({
          designId: '',
          title: '',
          description: '',
          orientation: 'horizontal',
          majorCategory: '',
          minorTags: '',
          price: '',
          product_type: 'Physical',
          isMockupOnly: false
        });
        setDesignImage(null);
        setDesignImagePreview(null);
        setMockups([]);
        setErrors({});

        // Redirect to admin page after a short delay
        setTimeout(() => {
          navigate('/admin');
        }, 1500);
      } else {
        // This should not happen as we return early on errors, but handle it just in case
        setToast({ message: 'Failed to create or update design', type: 'error' });
      }
    } catch (err) {
      console.error('Error creating design:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to create design';
      setToast({ message: errorMsg, type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
      navigate('/admin');
    }
  };

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-50">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="max-w-7xl mx-auto">
        <AdminHeader />

        <AdminTabs />

        {/* Quick Links */}
        <div className="mb-6 flex flex-wrap gap-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <a
              href="/admin/add-design"
              className="text-blue-700 hover:text-blue-900 font-medium underline flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Design
            </a>
          </div>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <a
              href="/admin/formats"
              className="text-blue-700 hover:text-blue-900 font-medium underline flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
              </svg>
              Formats
            </a>
          </div>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <button
              onClick={() => navigate('/admin', { state: { activeTab: 'json-editor' } })}
              className="text-blue-700 hover:text-blue-900 font-medium underline flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              Design Editor
            </button>
          </div>
        </div>

        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            {isEditMode ? 'Edit Design' : 'Add New Design'}
          </h1>
          <p className="text-sm text-gray-600">
            {isEditMode 
              ? 'Update design information. Upload a new image to replace the existing one, or keep the current image.'
              : 'Upload a design image and create a new design entry. You can optionally add mockups for mockup-only designs.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6">
          {/* Design ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Design ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="designId"
              value={formData.designId}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.designId ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., 20200105A0"
            />
            {errors.designId && (
              <p className="mt-1 text-sm text-red-600">{errors.designId}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">Only letters, numbers, underscores, and hyphens allowed</p>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Design Title"
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-600">{errors.title}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Design description (optional)"
            />
          </div>

          {/* Orientation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Orientation <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="orientation"
                  value="horizontal"
                  checked={formData.orientation === 'horizontal'}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                Horizontal
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="orientation"
                  value="vertical"
                  checked={formData.orientation === 'vertical'}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                Vertical
              </label>
            </div>
            {errors.orientation && (
              <p className="mt-1 text-sm text-red-600">{errors.orientation}</p>
            )}
          </div>

          {/* Design Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Design Image {!isEditMode && <span className="text-red-500">*</span>}
              {isEditMode && designImageFromPath && <span className="text-gray-500 text-xs ml-2">(Optional - upload to replace)</span>}
            </label>
            <div className="flex gap-4 items-start">
              {/* Image Preview on Left */}
              {designImagePreview && (
                <div className="flex-shrink-0">
                  <img
                    src={designImagePreview}
                    alt="Design preview"
                    className="w-48 h-48 object-contain border border-gray-300 rounded-md"
                    onError={(e) => {
                      // If image fails to load, show placeholder
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ddd" width="400" height="400"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="18" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage not found%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </div>
              )}
              
              {/* File Input Section on Right */}
              <div className="flex-1">
                {designImageFilename && (
                  <div className="mb-2 p-2 bg-gray-50 border border-gray-200 rounded-md">
                    <p className="text-xs text-gray-500 mb-1">Current file:</p>
                    <p className="text-sm font-mono text-gray-700">{designImageFilename}</p>
                    {designImageFromPath && (
                      <p className="text-xs text-blue-600 mt-1">(Loaded from existing design - upload new to replace)</p>
                    )}
                  </div>
                )}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                  onChange={handleDesignImageChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.designImage ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {isEditMode && designImageFromPath && (
                  <p className="mt-1 text-xs text-gray-500">Current image will be kept if no new file is uploaded</p>
                )}
                {!isEditMode && designImageFromPath && (
                  <p className="mt-1 text-xs text-gray-500">Upload a new file to replace the existing image</p>
                )}
                {errors.designImage && (
                  <p className="mt-1 text-sm text-red-600">{errors.designImage}</p>
                )}
              </div>
            </div>
          </div>

          {/* Tags Section */}
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags</h3>
            
            {/* Major Category */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Major Category
              </label>
              <select
                name="majorCategory"
                value={formData.majorCategory}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select category...</option>
                {majorCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Minor Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minor Tags
              </label>
              <input
                type="text"
                name="minorTags"
                value={formData.minorTags}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Comma-separated tags (e.g., tag1, tag2, tag3)"
              />
              <p className="mt-1 text-xs text-gray-500">Separate multiple tags with commas</p>
            </div>
          </div>

          {/* Mockup-Only Design Section */}
          <div className="border-t pt-4">
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="isMockupOnly"
                  checked={formData.isMockupOnly}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">Mockup-Only Design (Direct Purchase)</span>
              </label>
              <p className="mt-1 text-xs text-gray-500">Enable this if the design should be sold directly without format selection</p>
            </div>

            {formData.isMockupOnly && (
              <div className="space-y-4 pl-6 border-l-2 border-blue-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.price ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0.00"
                  />
                  {errors.price && (
                    <p className="mt-1 text-sm text-red-600">{errors.price}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Type
                  </label>
                  <select
                    name="product_type"
                    value={formData.product_type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Physical">Physical</option>
                    <option value="Digital">Digital</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Mockups Section */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Mockups
                {formData.isMockupOnly && <span className="text-red-500 ml-1">*</span>}
              </h3>
              <div className="flex gap-2">
                {formData.isMockupOnly && designImage && (
                  <button
                    type="button"
                    onClick={handleAddProductMockup}
                    className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                    title="Add 'Product' mockup using design image file and name"
                  >
                    Add "Product" Mockup
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleAddMockup}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Add Mockup
                </button>
              </div>
            </div>
            {formData.isMockupOnly && (
              <p className="text-sm text-gray-600 mb-4">
                At least one mockup is required for mockup-only designs
              </p>
            )}
            {errors.mockups && (
              <p className="text-sm text-red-600 mb-4">{errors.mockups}</p>
            )}

            {mockups.length === 0 ? (
              <p className="text-sm text-gray-500">No mockups added. Click "Add Mockup" to add one.</p>
            ) : (
              <div className="space-y-4">
                {mockups.map((mockup, index) => (
                  <div key={mockup.index} className="p-4 border border-gray-300 rounded-md">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-700">Mockup {index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => handleRemoveMockup(mockup.index)}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={mockup.name}
                          onChange={(e) => handleMockupChange(index, 'name', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors[`mockup_${index}_name`] ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Mockup name"
                        />
                        {errors[`mockup_${index}_name`] && (
                          <p className="mt-1 text-sm text-red-600">{errors[`mockup_${index}_name`]}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Image <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-4 items-start">
                          {/* Image Preview on Left */}
                          {mockup.preview && (
                            <div className="flex-shrink-0">
                              <img
                                src={mockup.preview}
                                alt="Mockup preview"
                                className="w-32 h-32 object-contain border border-gray-300 rounded-md"
                                onError={(e) => {
                                  // If image fails to load, show placeholder
                                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ddd" width="400" height="400"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="18" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage not found%3C/text%3E%3C/svg%3E';
                                }}
                              />
                            </div>
                          )}
                          
                          {/* File Input Section on Right */}
                          <div className="flex-1">
                            {mockup.imageUrl && mockup.fromPath && (
                              <div className="mb-2 p-2 bg-gray-50 border border-gray-200 rounded-md">
                                <p className="text-xs text-gray-500 mb-1">Current file:</p>
                                <p className="text-sm font-mono text-gray-700">{mockup.imageUrl.split('/').pop()}</p>
                                <p className="text-xs text-blue-600 mt-1">(Loaded from existing design - upload new to replace)</p>
                              </div>
                            )}
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                              onChange={(e) => handleMockupChange(index, 'image', e.target.files[0])}
                              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                errors[`mockup_${index}_image`] ? 'border-red-500' : 'border-gray-300'
                              }`}
                            />
                            {mockup.fromPath && !mockup.image && (
                              <p className="mt-1 text-xs text-gray-500">Using existing image. Upload a new file to replace it.</p>
                            )}
                            {errors[`mockup_${index}_image`] && (
                              <p className="mt-1 text-sm text-red-600">{errors[`mockup_${index}_image`]}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex gap-4 pt-4 border-t">
            <button
              type="submit"
              disabled={uploading}
              className="flex-1 px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (isEditMode ? 'Updating...' : 'Uploading...') : (isEditMode ? 'Update Design' : 'Create Design')}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={uploading}
              className="px-6 py-3 bg-gray-200 text-gray-700 font-medium rounded-md hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddDesignPage;
